import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { toast } from 'sonner';
import {
  ArrowLeft, Fire, Trophy, Target, ChartLineUp, Users,
  Check, Coins, TrendingUp, Lightning, Medal, Star,
  Calendar, Play, Crown, Sparkle, HandCoins
} from '@phosphor-icons/react';

const API = process.env.REACT_APP_BACKEND_URL;

// Challenge list data
const CHALLENGES = [
  { id: 0, text: "Read one article about index funds" },
  { id: 1, text: "Calculate your current net worth" },
  { id: 2, text: "Open a brokerage account" },
  { id: 3, text: "Set up auto-invest ($5/week)" },
  { id: 4, text: "Learn the difference between stocks and bonds" },
  { id: 5, text: "Research one low-cost ETF (e.g., VTI, VOO)" },
  { id: 6, text: "Create a simple monthly budget" },
  { id: 7, text: "Listen to a financial literacy podcast episode" },
  { id: 8, text: "Set a 1-year savings goal and write it down" },
  { id: 9, text: "Review your bank fees and eliminate one" },
  { id: 10, text: "Teach someone about compound interest" },
  { id: 11, text: "Plan your next 3 investment moves" }
];

// Sample community feed data
const SAMPLE_FEED = [
  { name: "Marcus", action: "just logged Week 12 💪", time: "2m ago" },
  { name: "Derrick", action: "joined the $5 Challenge", time: "5m ago" },
  { name: "Aisha", action: "hit her 26-week streak 🔥", time: "8m ago" },
  { name: "Tanya", action: "completed the Net Worth challenge", time: "12m ago" },
  { name: "Jerome", action: "just logged Week 4", time: "18m ago" },
  { name: "LaShawn", action: "earned the 12-Week Badge 🥈", time: "25m ago" }
];

// Badge milestones
const BADGES = [
  { week: 4, icon: "🥉", label: "4 Wk" },
  { week: 12, icon: "🥈", label: "12 Wk" },
  { week: 26, icon: "🥇", label: "26 Wk" },
  { week: 52, icon: "🏆", label: "52 Wk" }
];

// Calculate future value with compound interest
const calculateFutureValue = (weeklyAmount, weeks, annualRate = 0.07) => {
  const weeklyRate = annualRate / 52;
  let total = 0;
  for (let i = 0; i < weeks; i++) {
    total = (total + weeklyAmount) * (1 + weeklyRate);
  }
  return total;
};

const formatCurrency = (amount) => {
  if (amount >= 1000) {
    return `$${(amount / 1000).toFixed(1)}K`;
  }
  return `$${Math.round(amount).toLocaleString()}`;
};

const FiveMovementPage = () => {
  const { user, getAuthHeader } = useAuth();
  const navigate = useNavigate();
  
  // Personal tracker state
  const [hasPledged, setHasPledged] = useState(false);
  const [weeksLogged, setWeeksLogged] = useState(0);
  const [streak, setStreak] = useState(0);
  const [lastCheckIn, setLastCheckIn] = useState(null);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  
  // Challenge state
  const [completedChallenges, setCompletedChallenges] = useState([]);
  
  // Projector state
  const [projectorWeeks, setProjectorWeeks] = useState(52);
  
  // Community stats
  const [communityStats, setCommunityStats] = useState({
    members: 47,
    weeksLogged: 163,
    dollarsCommitted: 815,
    tenYearProjection: 242009
  });

  // Load user progress from API
  useEffect(() => {
    const fetchProgress = async () => {
      try {
        const [progressRes, statsRes] = await Promise.all([
          axios.get(`${API}/api/movement/progress`, { headers: getAuthHeader() }),
          axios.get(`${API}/api/movement/stats`)
        ]);
        
        const progress = progressRes.data;
        setHasPledged(progress.has_pledged || false);
        setWeeksLogged(progress.weeks_logged || 0);
        setStreak(progress.streak || 0);
        setLastCheckIn(progress.last_check_in || null);
        setCompletedChallenges(progress.completed_challenges || []);
        
        const stats = statsRes.data;
        setCommunityStats({
          members: stats.total_members,
          weeksLogged: stats.total_weeks_logged,
          dollarsCommitted: stats.total_dollars_committed,
          tenYearProjection: stats.ten_year_projection
        });
      } catch (error) {
        // Fallback to localStorage if API fails
        const savedData = localStorage.getItem('powr_5_movement');
        if (savedData) {
          const data = JSON.parse(savedData);
          setHasPledged(data.hasPledged || false);
          setWeeksLogged(data.weeksLogged || 0);
          setStreak(data.streak || 0);
          setLastCheckIn(data.lastCheckIn || null);
          setCompletedChallenges(data.completedChallenges || []);
        }
      } finally {
        setLoading(false);
      }
    };
    
    fetchProgress();
  }, [getAuthHeader]);

  // Handle pledge
  const handlePledge = async () => {
    setActionLoading(true);
    try {
      const response = await axios.post(`${API}/api/movement/pledge`, {}, { headers: getAuthHeader() });
      const data = response.data;
      setHasPledged(true);
      setWeeksLogged(data.weeks_logged);
      setStreak(data.streak);
      toast.success("Welcome to the $5 Movement! Let's build together 💪");
      
      // Refresh stats
      const statsRes = await axios.get(`${API}/api/movement/stats`);
      setCommunityStats({
        members: statsRes.data.total_members,
        weeksLogged: statsRes.data.total_weeks_logged,
        dollarsCommitted: statsRes.data.total_dollars_committed,
        tenYearProjection: statsRes.data.ten_year_projection
      });
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to pledge. Please try again.");
    } finally {
      setActionLoading(false);
    }
  };

  // Handle weekly check-in
  const handleCheckIn = async () => {
    setActionLoading(true);
    try {
      const response = await axios.post(`${API}/api/movement/checkin`, {}, { headers: getAuthHeader() });
      const data = response.data;
      
      const newWeeksLogged = data.weeks_logged;
      setWeeksLogged(newWeeksLogged);
      setStreak(data.streak);
      setLastCheckIn(data.last_check_in);
      
      // Refresh stats
      const statsRes = await axios.get(`${API}/api/movement/stats`);
      setCommunityStats({
        members: statsRes.data.total_members,
        weeksLogged: statsRes.data.total_weeks_logged,
        dollarsCommitted: statsRes.data.total_dollars_committed,
        tenYearProjection: statsRes.data.ten_year_projection
      });
      
      // Check for badge milestones
      const badge = BADGES.find(b => b.week === newWeeksLogged);
      if (badge) {
        toast.success(`🎉 You earned the ${badge.label} Badge! ${badge.icon}`);
      } else {
        toast.success(`Week ${newWeeksLogged} logged! Keep building 🔥`);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to check in. Please try again.");
    } finally {
      setActionLoading(false);
    }
  };

  // Handle challenge completion
  const toggleChallenge = async (challengeId) => {
    try {
      const response = await axios.post(
        `${API}/api/movement/challenge`, 
        { challenge_id: challengeId },
        { headers: getAuthHeader() }
      );
      
      const newCompleted = response.data.completed_challenges;
      setCompletedChallenges(newCompleted);
      
      if (newCompleted.includes(challengeId) && !completedChallenges.includes(challengeId)) {
        toast.success("Challenge completed! 💪");
      }
    } catch (error) {
      // Fallback to local state
      let newCompleted;
      if (completedChallenges.includes(challengeId)) {
        newCompleted = completedChallenges.filter(id => id !== challengeId);
      } else {
        newCompleted = [...completedChallenges, challengeId];
        toast.success("Challenge completed! 💪");
      }
      setCompletedChallenges(newCompleted);
    }
  };

  // Calculate projections
  const totalSaved = weeksLogged * 5;
  const estPortfolio = calculateFutureValue(5, weeksLogged);
  const progressPercent = Math.min(100, (weeksLogged / 52) * 100);
  
  // Projector calculations
  const proj5y = calculateFutureValue(5, projectorWeeks + 260);
  const proj10y = calculateFutureValue(5, projectorWeeks + 520);
  const proj20y = calculateFutureValue(5, projectorWeeks + 1040);
  const projDouble5y = calculateFutureValue(10, projectorWeeks + 260);
  const projDouble10y = calculateFutureValue(10, projectorWeeks + 520);
  const projDouble20y = calculateFutureValue(10, projectorWeeks + 1040);
  const projCurrent = calculateFutureValue(5, projectorWeeks);
  const projDouble = calculateFutureValue(10, projectorWeeks);
  const projInvested = projectorWeeks * 5;

  // Community momentum (simulated)
  const momentum = Math.min(100, Math.round((communityStats.weeksLogged / (communityStats.members * 4)) * 100));

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center">
        <div className="text-center">
          <Coins size={48} className="text-[#C4A661] mx-auto mb-4 animate-pulse" />
          <p className="text-[#A3A3A3]">Loading your progress...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0A0A0A]">
      {/* Header */}
      <header className="bg-[#0A0A0A] border-b border-[#1a1a1a] sticky top-0 z-40">
        <div className="max-w-6xl mx-auto px-4 md:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button 
                onClick={() => navigate('/community')}
                className="text-[#A3A3A3] hover:text-[#F7F7F5] transition-colors"
              >
                <ArrowLeft size={24} />
              </button>
              <div>
                <h1 className="text-xl font-bold text-[#F7F7F5] flex items-center gap-2">
                  <HandCoins size={24} className="text-[#C4A661]" />
                  The $5 Movement
                </h1>
                <p className="text-sm text-[#A3A3A3]">Build wealth $5 at a time</p>
              </div>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 md:px-8 py-8 space-y-8">
        {/* Personal Commitment Section */}
        <section>
          <div className="flex items-center gap-3 mb-6">
            <div className="px-3 py-1 bg-[#C4A661]/20 rounded-full">
              <span className="text-xs font-bold text-[#C4A661] uppercase tracking-wider">Your Commitment</span>
            </div>
            <h2 className="text-2xl font-bold text-[#F7F7F5]">Personal Tracker</h2>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            {/* Left: Pledge / Check-in */}
            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-gradient-to-br from-[#141414] to-[#0A0A0A] border border-[#C4A661]/30 rounded-2xl p-6"
            >
              {!hasPledged ? (
                <div className="text-center py-4">
                  <Coins size={48} className="text-[#C4A661] mx-auto mb-4" />
                  <p className="text-[#A3A3A3] mb-6 max-w-xs mx-auto">
                    Commit to investing $5 every week. Track your progress. Build your legacy.
                  </p>
                  <button 
                    onClick={handlePledge}
                    disabled={actionLoading}
                    className="w-full bg-[#C4A661] hover:bg-[#D4B671] text-[#0A0A0A] font-bold py-4 rounded-xl transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {actionLoading ? 'Joining...' : "I'm In — Start My $5 Journey"}
                  </button>
                </div>
              ) : (
                <div>
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="font-bold text-[#F7F7F5]">Week Check-In</h3>
                    <div className="flex items-center gap-2 bg-[#262626] px-3 py-1.5 rounded-full">
                      <Fire size={18} className="text-orange-500" weight="fill" />
                      <span className="font-bold text-[#F7F7F5]">{streak}</span>
                    </div>
                  </div>
                  
                  <button 
                    onClick={handleCheckIn}
                    disabled={actionLoading}
                    className="w-full bg-[#C4A661] hover:bg-[#D4B671] text-[#0A0A0A] font-bold py-4 rounded-xl transition-colors flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <Check size={20} weight="bold" />
                    {actionLoading ? 'Logging...' : "Log This Week's $5"}
                  </button>

                  <div className="grid grid-cols-2 gap-3 mt-4">
                    <div className="bg-[#1a1a1a] rounded-xl p-4 text-center">
                      <p className="text-2xl font-bold text-[#F7F7F5]">{weeksLogged}</p>
                      <p className="text-xs text-[#A3A3A3]">Weeks Logged</p>
                    </div>
                    <div className="bg-[#1a1a1a] rounded-xl p-4 text-center">
                      <p className="text-2xl font-bold text-[#C4A661]">${totalSaved}</p>
                      <p className="text-xs text-[#A3A3A3]">Total Committed</p>
                    </div>
                  </div>

                  <div className="bg-[#1a1a1a] rounded-xl p-4 text-center mt-3">
                    <p className="text-2xl font-bold text-[#C4A661]">${Math.round(estPortfolio).toLocaleString()}</p>
                    <p className="text-xs text-[#A3A3A3]">Est. Portfolio Value (7% return)</p>
                  </div>
                </div>
              )}
            </motion.div>

            {/* Right: Progress Ring */}
            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="bg-[#141414] border border-[#262626] rounded-2xl p-6 flex flex-col items-center justify-center"
            >
              <p className="text-xs text-[#A3A3A3] uppercase tracking-wider mb-4">52-Week Progress</p>
              
              {/* Progress Ring */}
              <div className="relative w-36 h-36 mb-6">
                <svg className="w-full h-full transform -rotate-90">
                  <circle
                    cx="72"
                    cy="72"
                    r="60"
                    stroke="#262626"
                    strokeWidth="12"
                    fill="none"
                  />
                  <circle
                    cx="72"
                    cy="72"
                    r="60"
                    stroke="#C4A661"
                    strokeWidth="12"
                    fill="none"
                    strokeLinecap="round"
                    strokeDasharray={377}
                    strokeDashoffset={377 - (377 * progressPercent / 100)}
                    className="transition-all duration-1000"
                  />
                </svg>
                <div className="absolute inset-0 flex flex-col items-center justify-center">
                  <span className="text-3xl font-bold text-[#F7F7F5]">{Math.round(progressPercent)}%</span>
                  <span className="text-xs text-[#A3A3A3]">of Year 1</span>
                </div>
              </div>

              {/* Badges */}
              <div className="flex gap-3">
                {BADGES.map((badge) => (
                  <div 
                    key={badge.week}
                    className={`flex flex-col items-center p-2 rounded-lg transition-all ${
                      weeksLogged >= badge.week 
                        ? 'bg-[#C4A661]/20 border border-[#C4A661]' 
                        : 'bg-[#1a1a1a] border border-[#262626] opacity-40'
                    }`}
                  >
                    <span className="text-xl">{badge.icon}</span>
                    <span className="text-[10px] text-[#A3A3A3] mt-1">{badge.label}</span>
                  </div>
                ))}
              </div>
            </motion.div>
          </div>
        </section>

        {/* Divider */}
        <div className="border-t border-[#1a1a1a]" />

        {/* Community Dashboard */}
        <section>
          <div className="flex items-center gap-3 mb-6">
            <div className="px-3 py-1 bg-[#8B5CF6]/20 rounded-full">
              <span className="text-xs font-bold text-[#8B5CF6] uppercase tracking-wider">Collective Power</span>
            </div>
            <h2 className="text-2xl font-bold text-[#F7F7F5]">Community Dashboard</h2>
          </div>

          {/* Stats Grid */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
            {[
              { label: "Members Pledged", value: communityStats.members.toLocaleString(), gold: true },
              { label: "Total Weeks Logged", value: communityStats.weeksLogged.toLocaleString() },
              { label: "Dollars Committed", value: `$${communityStats.dollarsCommitted.toLocaleString()}`, gold: true },
              { label: "10-Year Projection", value: `$${communityStats.tenYearProjection.toLocaleString()}` }
            ].map((stat, i) => (
              <motion.div 
                key={stat.label}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.1 }}
                className="bg-[#141414] border border-[#262626] rounded-xl p-4 text-center"
              >
                <p className={`text-2xl font-bold ${stat.gold ? 'text-[#C4A661]' : 'text-[#F7F7F5]'}`}>
                  {stat.value}
                </p>
                <p className="text-xs text-[#A3A3A3] mt-1">{stat.label}</p>
              </motion.div>
            ))}
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            {/* Community Feed */}
            <div className="bg-[#141414] border border-[#262626] rounded-2xl p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-bold text-[#F7F7F5]">Community Feed</h3>
                <div className="flex items-center gap-2">
                  <span className="w-2 h-2 bg-[#C4A661] rounded-full animate-pulse" />
                  <span className="text-xs text-[#C4A661]">Live</span>
                </div>
              </div>
              
              <div className="space-y-3">
                {SAMPLE_FEED.slice(0, 5).map((item, i) => (
                  <div key={i} className="flex items-start gap-3">
                    <span className="w-2 h-2 bg-[#C4A661] rounded-full mt-2 flex-shrink-0" />
                    <div>
                      <p className="text-sm text-[#F7F7F5]">
                        <span className="font-bold">{item.name}</span> {item.action}
                      </p>
                      <p className="text-xs text-[#525252]">{item.time}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Momentum Gauge */}
            <div className="bg-[#141414] border border-[#262626] rounded-2xl p-6 flex flex-col items-center justify-center">
              <p className="text-xs text-[#A3A3A3] uppercase tracking-wider mb-4">Community Momentum</p>
              
              {/* Gauge */}
              <div className="relative w-48 h-24 mb-2">
                <svg viewBox="0 0 200 110" className="w-full h-full">
                  <path 
                    d="M 20 100 A 80 80 0 0 1 180 100" 
                    fill="none" 
                    stroke="#262626" 
                    strokeWidth="12" 
                    strokeLinecap="round"
                  />
                  <path 
                    d="M 20 100 A 80 80 0 0 1 180 100" 
                    fill="none" 
                    stroke="#C4A661" 
                    strokeWidth="12" 
                    strokeLinecap="round"
                    strokeDasharray="251"
                    strokeDashoffset={251 - (251 * momentum / 100)}
                    className="transition-all duration-1000"
                  />
                </svg>
                <div className="absolute inset-0 flex items-center justify-center mt-4">
                  <span className="text-3xl font-bold text-[#C4A661]">{momentum}%</span>
                </div>
              </div>
              
              <p className="text-xs text-[#525252] text-center max-w-[220px]">
                Based on weekly participation rate across all members
              </p>
            </div>
          </div>
        </section>

        {/* Divider */}
        <div className="border-t border-[#1a1a1a]" />

        {/* Growth Projector */}
        <section>
          <div className="flex items-center gap-3 mb-6">
            <div className="px-3 py-1 bg-[#10B981]/20 rounded-full">
              <span className="text-xs font-bold text-[#10B981] uppercase tracking-wider">See Your Future</span>
            </div>
            <h2 className="text-2xl font-bold text-[#F7F7F5]">Growth Projector</h2>
          </div>

          <div className="bg-gradient-to-br from-[#141414] to-[#0A0A0A] border border-[#10B981]/30 rounded-2xl p-6">
            {/* Slider */}
            <div className="mb-6">
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs text-[#A3A3A3] uppercase tracking-wider">Weeks Invested</span>
                <span className="text-xl font-bold text-[#C4A661]">{projectorWeeks}</span>
              </div>
              <input 
                type="range" 
                min="1" 
                max="260" 
                value={projectorWeeks}
                onChange={(e) => setProjectorWeeks(parseInt(e.target.value))}
                className="w-full h-2 bg-[#262626] rounded-full appearance-none cursor-pointer accent-[#C4A661]"
                style={{
                  background: `linear-gradient(to right, #C4A661 0%, #C4A661 ${(projectorWeeks / 260) * 100}%, #262626 ${(projectorWeeks / 260) * 100}%, #262626 100%)`
                }}
              />
            </div>

            <div className="grid md:grid-cols-2 gap-6">
              {/* Projection Bars */}
              <div>
                <div className="flex items-end justify-between gap-4 h-40 mb-4">
                  {[
                    { label: "5 Yr", val: proj5y, valD: projDouble5y },
                    { label: "10 Yr", val: proj10y, valD: projDouble10y },
                    { label: "20 Yr", val: proj20y, valD: projDouble20y }
                  ].map((item, i) => {
                    const maxVal = projDouble20y;
                    const height = (item.val / maxVal) * 100;
                    const heightD = (item.valD / maxVal) * 100;
                    
                    return (
                      <div key={item.label} className="flex-1 flex flex-col items-center">
                        <p className="text-xs text-[#A3A3A3] mb-2">
                          {formatCurrency(item.val)} <span className="text-[#525252]">/ {formatCurrency(item.valD)}</span>
                        </p>
                        <div className="flex gap-1 items-end w-full justify-center h-28">
                          <div 
                            className="w-6 bg-[#C4A661] rounded-t transition-all duration-500"
                            style={{ height: `${Math.max(8, height)}%` }}
                          />
                          <div 
                            className="w-6 bg-[#C4A661]/40 rounded-t transition-all duration-500"
                            style={{ height: `${Math.max(8, heightD)}%` }}
                          />
                        </div>
                        <p className="text-xs text-[#A3A3A3] mt-2">{item.label}</p>
                      </div>
                    );
                  })}
                </div>
                
                {/* Legend */}
                <div className="flex items-center justify-center gap-6">
                  <div className="flex items-center gap-2">
                    <span className="w-3 h-3 bg-[#C4A661] rounded" />
                    <span className="text-xs text-[#A3A3A3]">$5/wk</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="w-3 h-3 bg-[#C4A661]/40 rounded" />
                    <span className="text-xs text-[#A3A3A3]">$10/wk</span>
                  </div>
                </div>
              </div>

              {/* Summary Cards */}
              <div className="space-y-3">
                <div className="bg-[#1a1a1a] rounded-xl p-4">
                  <p className="text-xs text-[#A3A3A3] uppercase tracking-wider mb-1">Current Pace (7% avg return)</p>
                  <p className="text-2xl font-bold text-[#C4A661]">${Math.round(projCurrent).toLocaleString()}</p>
                </div>
                <div className="bg-[#1a1a1a] rounded-xl p-4">
                  <p className="text-xs text-[#A3A3A3] uppercase tracking-wider mb-1">If You Double to $10/wk</p>
                  <p className="text-2xl font-bold text-[#C4A661]/70">${Math.round(projDouble).toLocaleString()}</p>
                </div>
                <div className="bg-[#1a1a1a] rounded-xl p-4">
                  <p className="text-xs text-[#A3A3A3] uppercase tracking-wider mb-1">Total Invested So Far</p>
                  <p className="text-2xl font-bold text-[#F7F7F5]">${projInvested.toLocaleString()}</p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Divider */}
        <div className="border-t border-[#1a1a1a]" />

        {/* Weekly Challenge Board */}
        <section>
          <div className="flex items-center gap-3 mb-6">
            <div className="px-3 py-1 bg-[#F59E0B]/20 rounded-full">
              <span className="text-xs font-bold text-[#F59E0B] uppercase tracking-wider">Level Up</span>
            </div>
            <h2 className="text-2xl font-bold text-[#F7F7F5]">Weekly Challenge Board</h2>
          </div>

          <div className="bg-[#141414] border border-[#262626] rounded-2xl p-6">
            {/* Progress Bar */}
            <div className="mb-6">
              <div className="flex items-center justify-between mb-2">
                <div className="h-2 flex-1 bg-[#262626] rounded-full overflow-hidden mr-4">
                  <div 
                    className="h-full bg-[#F59E0B] transition-all duration-500 rounded-full"
                    style={{ width: `${(completedChallenges.length / CHALLENGES.length) * 100}%` }}
                  />
                </div>
                <span className="text-sm text-[#A3A3A3]">{completedChallenges.length} / {CHALLENGES.length}</span>
              </div>
            </div>

            {/* Challenge List */}
            <div className="grid md:grid-cols-2 gap-3">
              {CHALLENGES.map((challenge) => {
                const isCompleted = completedChallenges.includes(challenge.id);
                return (
                  <button
                    key={challenge.id}
                    onClick={() => toggleChallenge(challenge.id)}
                    className={`flex items-center gap-3 p-4 rounded-xl text-left transition-all ${
                      isCompleted 
                        ? 'bg-[#F59E0B]/10 border border-[#F59E0B]/30' 
                        : 'bg-[#1a1a1a] border border-[#262626] hover:border-[#F59E0B]/30'
                    }`}
                  >
                    <div className={`w-6 h-6 rounded-md flex items-center justify-center flex-shrink-0 transition-colors ${
                      isCompleted ? 'bg-[#F59E0B] text-[#0A0A0A]' : 'bg-[#262626]'
                    }`}>
                      {isCompleted && <Check size={14} weight="bold" />}
                    </div>
                    <span className={`text-sm ${isCompleted ? 'text-[#F7F7F5]' : 'text-[#A3A3A3]'}`}>
                      {challenge.text}
                    </span>
                  </button>
                );
              })}
            </div>

            {/* Badges Earned */}
            {completedChallenges.length >= 4 && (
              <div className="mt-6 pt-6 border-t border-[#262626]">
                <p className="text-xs text-[#A3A3A3] uppercase tracking-wider mb-3">Badges Earned</p>
                <div className="flex gap-3">
                  {completedChallenges.length >= 4 && (
                    <div className="flex items-center gap-2 px-3 py-1.5 bg-[#F59E0B]/20 border border-[#F59E0B]/30 rounded-full">
                      <span>🎯</span>
                      <span className="text-xs text-[#F59E0B]">Starter</span>
                    </div>
                  )}
                  {completedChallenges.length >= 8 && (
                    <div className="flex items-center gap-2 px-3 py-1.5 bg-[#F59E0B]/20 border border-[#F59E0B]/30 rounded-full">
                      <span>⭐</span>
                      <span className="text-xs text-[#F59E0B]">Committed</span>
                    </div>
                  )}
                  {completedChallenges.length >= 12 && (
                    <div className="flex items-center gap-2 px-3 py-1.5 bg-[#F59E0B]/20 border border-[#F59E0B]/30 rounded-full">
                      <span>🏆</span>
                      <span className="text-xs text-[#F59E0B]">Champion</span>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </section>
      </main>
    </div>
  );
};

export default FiveMovementPage;
